import React, { useEffect, useMemo, useState } from 'react';
import { Camera, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/feedback';

const STANDARD_OPTIONS = ['Nursery', 'LKG', 'UKG', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'DEGREE', 'PG', 'OTHER'];
const DIVISION_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];
const HOUSE_OPTIONS = ['Red', 'Green', 'Blue', 'Yellow'];

const createInitialFormData = (data = {}) => ({
  serial_no: data.serial_no ?? '',
  student_name: data.student_name ?? '',
  std_roman: data.std_roman ?? '',
  division: data.division ?? '',
  custom_div: data.custom_div ?? '',
  fathers_name: data.fathers_name ?? '',
  dob: data.dob ?? '',
  reg_no: data.reg_no ?? '',
  appar_id: data.appar_id ?? '',
  sat_no: data.sat_no ?? '',
  house: data.house ?? 'Red',
  address: data.address ?? '',
  mobile: data.mobile ?? '',
});

const StudentForm = ({ onAdd, initialData, isEditing = false, onCancelEdit, onSaved }) => {
  const { fieldPermissions } = useAuth();
  const [formData, setFormData] = useState(createInitialFormData(initialData));
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(initialData?.photo_url || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(createInitialFormData(initialData));
    setPreview(initialData?.photo_url || null);
    setPhotoFile(null);
  }, [initialData]);

  const isFieldVisible = (key) => !!fieldPermissions?.[key] || ['student_name', 'std_roman'].includes(key);

  const canSave = useMemo(() => String(formData.student_name).trim() && String(formData.std_roman).trim(), [formData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast({ icon: 'error', title: 'Please select an image file.' });
      return;
    }

    setPhotoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setFormData(createInitialFormData());
    setPhotoFile(null);
    setPreview(null);
  };

  const handleSave = async () => {
    if (!canSave) {
      showToast({ icon: 'warning', title: 'Please enter Student Name and Standard.' });
      return;
    }

    // --- പുതിയ ലോജിക്: എന്താണ് അപ്ഡേറ്റ് ചെയ്തത് എന്ന് കണ്ടെത്താൻ ---
    let updateType = null;
    if (isEditing) {
      const isPhotoUpdated = !!photoFile; // പുതിയ ഫോട്ടോ സെലക്ട് ചെയ്തിട്ടുണ്ടെങ്കിൽ
      
      // ടെക്സ്റ്റ് ഫീൽഡുകളിൽ എന്തെങ്കിലും മാറ്റം വന്നിട്ടുണ്ടോ എന്ന് ചെക്ക് ചെയ്യുന്നു
      const isDataUpdated = Object.keys(formData).some((key) => {
        const oldValue = String(initialData?.[key] || '').trim();
        const newValue = String(formData[key] || '').trim();
        return oldValue !== newValue;
      });

      if (isPhotoUpdated && isDataUpdated) updateType = 'both';
      else if (isPhotoUpdated) updateType = 'photo';
      else if (isDataUpdated) updateType = 'data';
    }
    // -----------------------------------------------------------------

    try {
      setSaving(true);

      await onAdd(
        {
          ...formData,
          serial_no: formData.serial_no ? Number(formData.serial_no) : null,
          student_name: String(formData.student_name).trim(),
          std_roman: String(formData.std_roman).trim(),
          division: String(formData.division).trim(),
          custom_div: String(formData.custom_div).trim(),
          fathers_name: String(formData.fathers_name).trim(),
          dob: String(formData.dob).trim(),
          reg_no: String(formData.reg_no).trim(),
          appar_id: String(formData.appar_id).trim(),
          sat_no: String(formData.sat_no).trim(),
          house: String(formData.house).trim() || 'Red',
          address: String(formData.address).trim(),
          mobile: String(formData.mobile).trim(),
          photo_name: photoFile?.name || initialData?.photo_name || '',
          originalPhotoName:
            photoFile?.name || initialData?.originalPhotoName || initialData?.photo_name || '',
          photo_url: initialData?.photo_url || '',
          update_type: updateType, // <-- പുതുതായി ആഡ് ചെയ്തത്
        },
        photoFile
      );

      showToast({ icon: 'success', title: isEditing ? 'Student updated successfully.' : 'Student added successfully.' });

      if (isEditing) {
        onCancelEdit?.();
      } else {
        resetForm();
      }

      onSaved?.();
    } catch (err) {
      showToast({ icon: 'error', title: err?.message || 'Failed to save student.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-panel student-form-panel">
      <div className="app-panel-header d-flex justify-content-between align-items-start gap-3">
        <div>
          <h3>{isEditing ? 'Edit Student' : 'Add New Student'}</h3>
          <p>Use the same fields and logic, now with cleaner spacing and clearer focus states.</p>
        </div>

        {(isEditing || onCancelEdit) && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm rounded-pill px-3"
            onClick={() => {
              onCancelEdit?.();
              resetForm();
            }}
          >
            {isEditing ? 'Cancel Edit' : 'Close'}
          </button>
        )}
      </div>

      <div className="card-body p-0">
        {isFieldVisible('photo_url') && (
          <div className="photo-picker mb-4">
            <div className="photo-preview-wrap">
              <img src={preview || 'https://placehold.co/100'} className="photo-preview" alt="preview" />
            </div>
            <div className="photo-picker-content">
              <label className="photo-upload-btn">
                <Camera size={16} />
                <span>{photoFile ? 'Change Photo' : 'Upload Photo'}</span>
                <input type="file" accept="image/*" onChange={handlePickPhoto} hidden />
              </label>
              {!!(photoFile?.name || initialData?.originalPhotoName) && (
                <small className="text-muted d-block mt-2">
                  Current file name: <strong>{photoFile?.name || initialData?.originalPhotoName}</strong>
                </small>
              )}
            </div>
          </div>
        )}

        <div className="row g-3">
          {isFieldVisible('serial_no') && (
            <div className="col-md-4">
              <label className="form-label">Serial No</label>
              <input value={formData.serial_no} type="number" className="form-control app-input" onChange={(e) => handleChange('serial_no', e.target.value)} />
            </div>
          )}

          <div className="col-md-8">
            <label className="form-label">Student Name *</label>
            <input value={formData.student_name} type="text" className="form-control app-input" onChange={(e) => handleChange('student_name', e.target.value)} />
          </div>

          <div className="col-md-4">
            <label className="form-label">Standard *</label>
            <select value={formData.std_roman} className="form-select app-input" onChange={(e) => handleChange('std_roman', e.target.value)}>
              <option value="">Select</option>
              {STANDARD_OPTIONS.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          {isFieldVisible('division') && (
            <div className="col-md-4">
              <label className="form-label">Division</label>
              <select value={formData.division} className="form-select app-input" onChange={(e) => handleChange('division', e.target.value)}>
                <option value="">Select</option>
                {DIVISION_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          )}

          {isFieldVisible('custom_div') && (
            <div className="col-md-4">
              <label className="form-label">Custom Div</label>
              <input value={formData.custom_div} type="text" className="form-control app-input" onChange={(e) => handleChange('custom_div', e.target.value)} />
            </div>
          )}

          {isFieldVisible('fathers_name') && (
            <div className="col-12">
              <label className="form-label">Father&apos;s Name</label>
              <input value={formData.fathers_name} type="text" className="form-control app-input" onChange={(e) => handleChange('fathers_name', e.target.value)} />
            </div>
          )}

          {isFieldVisible('dob') && (
            <div className="col-md-4">
              <label className="form-label">DOB</label>
              <input value={formData.dob} type="text" placeholder="DD MM YYYY" className="form-control app-input" onChange={(e) => handleChange('dob', e.target.value)} />
            </div>
          )}

          {isFieldVisible('reg_no') && (
            <div className="col-md-4">
              <label className="form-label">Reg No</label>
              <input value={formData.reg_no} type="text" className="form-control app-input" onChange={(e) => handleChange('reg_no', e.target.value)} />
            </div>
          )}

          {isFieldVisible('appar_id') && (
            <div className="col-md-4">
              <label className="form-label">Pen No / Appar Id</label>
              <input value={formData.appar_id} type="text" className="form-control app-input" onChange={(e) => handleChange('appar_id', e.target.value)} />
            </div>
          )}

          {isFieldVisible('sat_no') && (
            <div className="col-md-4">
              <label className="form-label">SAT No</label>
              <input value={formData.sat_no} type="text" className="form-control app-input" onChange={(e) => handleChange('sat_no', e.target.value)} />
            </div>
          )}

          {isFieldVisible('house') && (
            <div className="col-md-4">
              <label className="form-label">House</label>
              <select value={formData.house} className="form-select app-input" onChange={(e) => handleChange('house', e.target.value)}>
                {HOUSE_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          )}

          {isFieldVisible('mobile') && (
            <div className="col-md-4">
              <label className="form-label">Mobile</label>
              <input value={formData.mobile} type="text" className="form-control app-input" onChange={(e) => handleChange('mobile', e.target.value)} />
            </div>
          )}

          {isFieldVisible('address') && (
            <div className="col-12">
              <label className="form-label">Address</label>
              <textarea value={formData.address} className="form-control app-input" rows="3" onChange={(e) => handleChange('address', e.target.value)} />
            </div>
          )}
        </div>

        <button type="button" className="btn btn-primary w-100 mt-4 app-primary-btn" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? (
            <span className="d-inline-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              {isEditing ? 'Updating...' : 'Saving...'}
            </span>
          ) : (
            <span className="d-inline-flex align-items-center gap-2 justify-content-center">
              <Save size={16} />
              {isEditing ? 'Update Student' : 'Save Student'}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default StudentForm;